// imports
import React, { FC, useEffect } from 'react';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { Alert } from '../../../shared/lib/common/Alert';
import { yupResolver } from '@hookform/resolvers/yup';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { StripeCardCvcElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
// components
import { CustomController } from '../../../shared/lib/common/CustomController';
import { StripeTextFieldCVC, StripeTextFieldExpiry, StripeTextFieldNumber } from './StripeFields';
// interfaces
import { CardDetail, CardPaymentProps, NewCreateSubscriptionData } from '../app/interfaceTypes';
import { MSG_CARD_ELEMENT_NOT_FOUND } from '../app/constants';
import { LABEL_NAME_ON_CARD, LABEL_CARD_NUMBER, LABEL_EXPIRY_MM_YY, LABEL_CVC } from '../../../shared/lib/constants';
// schemas
import { cardValidationScehma } from '../app/validationSchema';

const CardPaymentModal: FC<CardPaymentProps> = ({ createSubscription, cardSubmit, onError }) => {
  const elements = useElements();
  const stripe = useStripe();
  countries.registerLocale(enLocale);

  const methods = useForm<CardDetail>({
    resolver: yupResolver(cardValidationScehma),
    defaultValues: { name: '', cvc: '', expiry: '', number: '' }
  });

  const billingMethods = useFormContext<NewCreateSubscriptionData>();
  const { watch } = billingMethods;
  const { cardDetail } = watch();
  const { address_city, address_line1, address_line2, address_state, address_zip, country } = cardDetail || {};
  const { setError, formState, handleSubmit } = methods;
  const { errors } = formState;
  const { cvc, expiry, number: cardNumber } = errors || {};

  const onCardChange = (event: StripeCardNumberElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('number', { message, type: 'validate' });
    complete && !message && setError('number', {});
    empty &&
      setError('number', {
        message: 'Card No is requried',
        type: 'validate'
      });
  };

  const onExpiryChange = (event: StripeCardExpiryElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('expiry', { message, type: 'validate' });
    complete && !message && setError('expiry', {});
    empty &&
      setError('expiry', {
        message: 'Expiry Date is Required',
        type: 'validate'
      });
  };

  const onCVCChange = (event: StripeCardCvcElementChangeEvent) => {
    const { complete, error, empty } = event;
    const { message } = error || {};
    message && setError('cvc', { message, type: 'validate' });
    complete && !message && setError('cvc', {});
    empty && setError('cvc', { message: 'cvc is required', type: 'validate' });
  };

  const onSubmit = async (data: CardDetail) => {
    const { name } = data;

    if (elements?.getElement) {
      const cardElement = elements.getElement(CardNumberElement);

      if (!cardElement) {
        Alert.error(MSG_CARD_ELEMENT_NOT_FOUND);
        onError();
        return;
      }

      const res = await stripe?.createPaymentMethod({
        element: cardElement,
        params: {
          billing_details: {
            name,
            address: {
              city: address_city ?? '',
              country: countries.getAlpha2Code(country ?? 'US', 'en'),
              line1: address_line1 ?? '',
              line2: address_line2 ?? '',
              state: address_state ?? '',
              postal_code: address_zip ?? ''
            }
          }
        }
      });

      const { paymentMethod, error } = res || {};

      if (error) {
        const { message } = error;
        onError();
        Alert.error(message || '');
      }
      if (paymentMethod?.id) {
        await createSubscription(paymentMethod.id || '');
      }
    }
  };

  useEffect(() => {
    if (cardSubmit) {
      handleSubmit(onSubmit, () => {
        onError();
      })();
    }
  }, [cardSubmit]);

  return (
    <>
      <FormProvider {...methods}>
        <Grid container spacing={2} rowSpacing={2}>
          <Grid item xs={12}>
            <CustomController controllerLabel={LABEL_NAME_ON_CARD} controllerName="name" />
          </Grid>

          <Grid item xs={12}>
            <StripeTextFieldNumber
              margin="dense"
              label={LABEL_CARD_NUMBER}
              onChange={onCardChange}
              error={Boolean(cardNumber?.message)}
              labelErrorMessage={cardNumber?.message}
              inputProps={{
                options: {
                  showIcon: true
                }
              }}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <StripeTextFieldExpiry margin="dense" label={LABEL_EXPIRY_MM_YY} onChange={onExpiryChange} error={Boolean(expiry?.message)} labelErrorMessage={expiry?.message} />
          </Grid>

          <Grid item md={6} xs={12}>
            <StripeTextFieldCVC margin="dense" label={LABEL_CVC} onChange={onCVCChange} error={Boolean(cvc?.message)} labelErrorMessage={cvc?.message} />
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default CardPaymentModal;
